import type { YjsRegistry } from '@/lib/sync/yjs-registry';
import { useEntityStore } from '@/lib/sync/entity-store';

export const LRU_DOC_CAP = 50;

interface LruEntry {
  id: string;
  ts: number;
}

function hasPendingOps(entityId: string): boolean {
  return useEntityStore.getState().pendingOps.some(
    (p) => p.ops.some((op) => op.entityId === entityId),
  );
}

export class LruTracker {
  private entries: LruEntry[] = [];

  constructor(
    private userId: string,
    private registry: Pick<YjsRegistry, 'getEntry'>,
  ) {
    this.load();
  }

  touch(id: string): void {
    // Remove existing entry for this id if present
    this.entries = this.entries.filter((e) => e.id !== id);
    // Prepend to head (most recently used)
    this.entries.unshift({ id, ts: Date.now() });
    // Check if we exceeded the cap
    if (this.entries.length > LRU_DOC_CAP) {
      this.evictLru();
    }
    this.persist();
  }

  private evictLru(): void {
    // Iterate from the tail (least recently used) upward, find first safe candidate
    for (let i = this.entries.length - 1; i >= 0; i--) {
      const entry = this.entries[i];
      const registryEntry = this.registry.getEntry(entry.id);
      const isInUse = registryEntry !== undefined && registryEntry.refCount > 0;
      const hasPending = hasPendingOps(entry.id);
      if (isInUse || hasPending) continue;
      // Safe to evict
      indexedDB.deleteDatabase(`nula-sync-${entry.id}`);
      this.entries.splice(i, 1);
      return;
    }
    // No safe candidate — soft cap, do not throw
  }

  private load(): void {
    const raw = localStorage.getItem(`nula-lru-${this.userId}`);
    if (!raw) return;
    try {
      this.entries = JSON.parse(raw) as LruEntry[];
    } catch {
      this.entries = [];
    }
  }

  private persist(): void {
    localStorage.setItem(`nula-lru-${this.userId}`, JSON.stringify(this.entries));
  }
}
